import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import { useEffect, useRef } from "react";
import { COLORS } from "../ui.js";

interface Props {
  doc: unknown;
  onChange: (doc: unknown) => void;
  placeholder?: string;
}

/**
 * Éditeur WYSIWYG basé sur Tiptap (ProseMirror).
 * Stocke le contenu au format Tiptap JSON dans block.props.doc.
 *
 * La toolbar flottante apparaît au survol/focus avec les actions essentielles :
 * Gras, Italique, Lien, H1/H2, Liste, Citation.
 */
export function TiptapEditor({ doc, onChange, placeholder }: Props) {
  const skipNext = useRef(false);
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      Placeholder.configure({ placeholder: placeholder ?? "Écrivez ici…" }),
    ],
    content: (doc as object) ?? { type: "doc", content: [] },
    onUpdate: ({ editor }) => {
      skipNext.current = true;
      onChange(editor.getJSON());
    },
  });

  // Sync externe → éditeur (utile quand on switch de bloc sélectionné)
  useEffect(() => {
    if (!editor) return;
    if (skipNext.current) {
      skipNext.current = false;
      return;
    }
    const current = editor.getJSON();
    if (JSON.stringify(current) !== JSON.stringify(doc)) {
      editor.commands.setContent((doc as object) ?? { type: "doc", content: [] }, { emitUpdate: false });
    }
  }, [doc, editor]);

  if (!editor) return null;

  return (
    <div>
      <div
        style={{
          display: "flex",
          gap: 2,
          padding: 4,
          background: "#fafaf8",
          border: `1px solid ${COLORS.border}`,
          borderRadius: 6,
          marginBottom: 8,
        }}
      >
        <TbBtn active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()}><b>B</b></TbBtn>
        <TbBtn active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()}><i>I</i></TbBtn>
        <TbBtn active={editor.isActive("strike")} onClick={() => editor.chain().focus().toggleStrike().run()}><s>S</s></TbBtn>
        <Sep />
        <TbBtn active={editor.isActive("heading", { level: 1 })} onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}>H1</TbBtn>
        <TbBtn active={editor.isActive("heading", { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}>H2</TbBtn>
        <TbBtn active={editor.isActive("paragraph")} onClick={() => editor.chain().focus().setParagraph().run()}>¶</TbBtn>
        <Sep />
        <TbBtn active={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()}>• Liste</TbBtn>
        <TbBtn active={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()}>1. Liste</TbBtn>
        <TbBtn active={editor.isActive("blockquote")} onClick={() => editor.chain().focus().toggleBlockquote().run()}>&ldquo;</TbBtn>
      </div>
      <div
        style={{
          border: `1px solid ${COLORS.border}`,
          borderRadius: 6,
          padding: 12,
          background: "#fff",
          minHeight: 120,
          fontSize: 14,
          lineHeight: 1.6,
        }}
      >
        <EditorContent editor={editor} />
      </div>
      <style>{`
        .ProseMirror:focus { outline: none; }
        .ProseMirror p { margin: 0 0 8px; }
        .ProseMirror h1 { font-size: 24px; font-weight: 700; margin: 16px 0 8px; }
        .ProseMirror h2 { font-size: 19px; font-weight: 700; margin: 14px 0 8px; }
        .ProseMirror h3 { font-size: 16px; font-weight: 700; margin: 12px 0 6px; }
        .ProseMirror ul, .ProseMirror ol { padding-left: 24px; }
        .ProseMirror blockquote {
          border-left: 3px solid ${COLORS.border};
          padding-left: 12px;
          color: ${COLORS.muted};
          margin: 12px 0;
        }
        .ProseMirror p.is-editor-empty:first-child::before {
          color: ${COLORS.muted};
          content: attr(data-placeholder);
          float: left;
          height: 0;
          pointer-events: none;
        }
      `}</style>
    </div>
  );
}

function TbBtn({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      type="button"
      style={{
        padding: "4px 10px",
        border: "none",
        background: active ? COLORS.text : "transparent",
        color: active ? "#fff" : COLORS.text,
        cursor: "pointer",
        borderRadius: 4,
        fontSize: 13,
        fontFamily: "inherit",
        minWidth: 28,
      }}
    >
      {children}
    </button>
  );
}

function Sep() {
  return <div style={{ width: 1, background: COLORS.border, margin: "4px 2px" }} />;
}
